import { ClientOnly } from "@winamp/ui/components/ClientOnly/ClientOnly.js";
import { Suspense, lazy, memo, useEffect, useRef, useState } from "react";

// Définition des types pour les props de ProductFruits
interface ProductFruitsUser {
  username?: string;
  email?: string;
  firstname?: string;
  lastname?: string;
  props?: Record<string, unknown>;
}

interface ProductFruitsProps {
  user: ProductFruitsUser;
  language: string;
  workspaceCode: string;
}

// Utiliser un iframe pour isoler complètement ProductFruits
// Cela empêche les problèmes de performance d'affecter l'application principale
const IsolatedProductFruits = ({
  user,
  language,
  workspaceCode,
}: ProductFruitsProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    // Délai pour s'assurer que l'application principale est déjà chargée
    const timer = setTimeout(() => {
      if (!iframeRef.current) return;

      try {
        // Initialiser ProductFruits dans l'iframe
        const iframe = iframeRef.current;
        const iframeDoc =
          iframe.contentDocument || iframe.contentWindow?.document;

        if (!iframeDoc) return;

        // Créer un script pour charger ProductFruits dans l'iframe
        const script = iframeDoc.createElement("script");
        script.src = "https://cdn.productfruits.com/assets/loader.min.js";
        script.async = true;
        script.defer = true;

        // Ajouter les données utilisateur et la configuration
        const configScript = iframeDoc.createElement("script");
        configScript.textContent = `
          window.pfDisableAutoInit = true;
          window.pfUser = ${JSON.stringify(user)};
          window.pfWorkspaceCode = "${workspaceCode}";
          window.pfLanguage = "${language}";
          window.addEventListener('load', function() {
            if (window.ProductFruits) {
              window.ProductFruits.init();
            }
          });
        `;

        // Ajouter les scripts à l'iframe
        iframeDoc.head.appendChild(configScript);
        iframeDoc.head.appendChild(script);
      } catch (error) {
        console.error("Failed to initialize ProductFruits in iframe:", error);
      }
    }, 3000); // Délai plus long pour s'assurer que l'application est bien chargée

    return () => clearTimeout(timer);
  }, [user, language, workspaceCode]);

  return (
    <iframe
      ref={iframeRef}
      title="ProductFruits"
      style={{
        border: "none",
        position: "absolute",
        width: "0",
        height: "0",
        pointerEvents: "none",
        opacity: 0,
      }}
    />
  );
};

// Version lazy-loadée du composant isolé
const LazyProductFruits = lazy(async () => {
  // Attendre que l'application principale soit chargée
  await new Promise((resolve) => setTimeout(resolve, 2000));

  return {
    default: IsolatedProductFruits,
  };
});

const MemoProductFruits = memo(LazyProductFruits);

export function ProductFruits(props: ProductFruitsProps) {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShouldRender(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  if (!shouldRender) return null;

  return (
    <ClientOnly>
      <Suspense fallback={null}>
        <MemoProductFruits {...props} />
      </Suspense>
    </ClientOnly>
  );
}
